import React, { useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/react-hooks';

import './wish-list-button-style.scss';
import { CredentialContext, credentialType } from '@providers';
import { useSnackbar } from '@src/providers/SnackbarProvider';

import Typography from '@src/componentsNew/Typography/Typography';
import WishlistIcon from '@src/componentsNew/Icons/Icons/WishlistIcon';

// Queries
import { WISHLIST_ADD, WISHLIST_DELETE } from '@src/pages/WishList/queries';
import { addWishlist } from '@src/pages/WishList/__generated__/addWishlist';
import { deleteWishlist } from '@src/pages/WishList/__generated__/deleteWishlist';

import { gaV4AddToWishList, gaV4RemoveFromWishList, getDBIdFromGraphqlId } from '@src/core/utils';
import { redirectToLoginWithCurrentPath } from '@src/utils/authCheck';
import { wishListUrl } from '@src/app/routes';

export interface IProps {
  wishListTotalCount?: number;
  isShowWishListTotalCount?: boolean;
  isSelected: boolean;
  productId: string;
  width?: string;
  productName: string;
  previousPage: string;
  previousPath: string;
  productPrice: number;
}

const WishListButton = ({
  wishListTotalCount,
  isShowWishListTotalCount,
  isSelected,
  productId,
  width,
  productName,
  previousPage,
  previousPath,
  productPrice,
}: IProps) => {
  const history = useHistory();
  const { showSnackbar } = useSnackbar();
  const { currentUser } = useContext<credentialType>(CredentialContext);

  const [selected, setSelected] = useState(isSelected);
  const [totalCount, setTotalDiscount] = useState(wishListTotalCount || 0);

  const [addWishList, { loading: addWishListLoading }] = useMutation<addWishlist>(WISHLIST_ADD, {
    onCompleted: (data) => {
      if (data?.wishlistAddProduct?.errors?.length) {
        showSnackbar({ message: 'Failed to add wishlist' });
        return;
      }

      setSelected(true);
      setTotalDiscount((prev) => prev + 1);

      showSnackbar({
        message: 'Product Added to Wishlist',
        autoHideDuration: 1500,
        isShowBtn: true,
        btnContent: 'Wishlist',
        btnOnClick: () => history.push(wishListUrl),
      });

      const wishlistProductName = productName;
      const wishlistProductId = getDBIdFromGraphqlId(productId);
      const wishlistProductPrice = productPrice;

      gaV4AddToWishList(previousPage, wishlistProductId, wishlistProductName, wishlistProductPrice);
    },
  });

  const [deleteWishList, { loading: deleteWishListLoading }] = useMutation<deleteWishlist>(WISHLIST_DELETE, {
    onCompleted: (data) => {
      if (data?.wishlistDeleteItemByProduct?.errors?.length) {
        showSnackbar({ message: 'Failed to remove wishlist' });
        return;
      }

      setSelected(false);
      setTotalDiscount((prev) => {
        if (prev > 0) return prev - 1;
      });

      showSnackbar({
        message: 'Removed from Wishlist',
        autoHideDuration: 1500,
      });

      const wishlistProductId = getDBIdFromGraphqlId(productId);
      const wishlistProductPrice = productPrice;

      gaV4RemoveFromWishList(previousPage, wishlistProductId, wishlistProductPrice);
    },
  });

  const handleSelectedButton = (event) => {
    event.stopPropagation();

    if (addWishListLoading || deleteWishListLoading) {
      return;
    }

    if (currentUser?.token) {
      if (selected === false) {
        addWishList({ variables: { productId } });
      } else {
        deleteWishList({ variables: { productId } });
      }
    } else {
      redirectToLoginWithCurrentPath(history);
    }
  };

  return (
    <div className="wishlist-btn" onClick={handleSelectedButton}>
      <WishlistIcon
        width={width}
        height={width}
        fillColor={selected ? '#212121' : ''}
        strokeColor={selected ? '#212121' : ''}
      />

      {isShowWishListTotalCount && (
        <Typography variant="caption" component="span" style={{ color: '#666666' }}>
          {totalCount ? totalCount : '0'}
        </Typography>
      )}
    </div>
  );
};

export default WishListButton;
